import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { HStack, VStack, Flex, Box, Button } from '@cad-arc';
import { BoloSearchOptions, EventSearchOptions, UnitStatusSearchOptions } from '@mark43/cad-api';
import { useEffect, useState } from 'react';
import { FormInput } from '../forms/integrated-form-controls/form-input';
import { FormRadioGroup } from '../forms/integrated-form-controls/form-radio-group';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { FormSection } from '../search-windows/form-section';
import { FormNInputGroups } from '../forms/integrated-form-controls/form-n-input-groups';
import {
    INPUT_WIDTH_SM,
    INPUT_WIDTH_MD,
    INPUT_PERCENTAGE_WIDTH_SM,
    INPUT_PERCENTAGE_WIDTH_MD,
} from '../search-windows/constants';
import { useSubPremiseFieldLabels } from '../common/hooks/use-sub-premise-field-labels';
import { useIntegratedForm } from '../forms/use-integrated-form';
import { SearchLocationForm } from './types';
import { SearchFormRadiusSearchSection } from './search-form-radius-search-section';
import { SearchFormPolygonSearchSection } from './search-form-polygon-search-section';
import { useApplicationType } from '../common/hooks/use-application-type';

export function SearchFormLocationSection({
    title,
    options,
    hideLocationVerificationField,
}: {
    title: string;
    options: EventSearchOptions | BoloSearchOptions | UnitStatusSearchOptions;
    hideLocationVerificationField?: boolean;
}) {
    const { form } = useIntegratedForm<SearchLocationForm>();
    const [showDetails, setShowDetails] = useState<boolean>(false);
    const { subpremiseSupport } = useEnabledFeatures();
    const { subPremiseNameLabel, subPremiseValueLabel } = useSubPremiseFieldLabels();
    const { isFirstResponder } = useApplicationType();

    useEffect(
        function clearHiddenFields() {
            if (!showDetails) {
                form.resetField('locations.0.latitude');
                form.resetField('locations.0.longitude');
                form.resetField('locations.0.radiusInFeet');
                form.resetField('polygonPoints');
            }
        },
        [showDetails, form]
    );

    return (
        <VStack align="left" data-testid="search-location-section" gap={4}>
            <FormSection title={title} width="100%">
                <Flex flex={1} flexFlow="wrap" gap={4}>
                    <Flex flex={0.5} flexFlow="wrap" gap={4}>
                        <Box
                            w="70%"
                            minWidth={INPUT_WIDTH_MD}
                            maxWidth={subpremiseSupport ? 'auto' : '15rem'}
                            flex={1}
                        >
                            <FormInput
                                name="locations.0.streetAddress"
                                label="Street Address"
                                control={form.control}
                            />
                        </Box>
                        {!subpremiseSupport && (
                            <Box w="5rem">
                                <FormInput
                                    name="locations.0.subPremise"
                                    label="Apt, Unit"
                                    excludeFromFuzzySearch
                                    control={form.control}
                                />
                            </Box>
                        )}
                    </Flex>
                    {subpremiseSupport && (
                        <FormNInputGroups
                            groupName="locations.0.subPremiseDtos"
                            groupLabel="Sub-premise"
                            properties={[
                                {
                                    propertyLabel: subPremiseNameLabel,
                                    propertyName: 'subPremiseName',
                                },
                                {
                                    propertyLabel: subPremiseValueLabel,
                                    propertyName: 'subPremiseValue',
                                },
                            ]}
                            buttonSize={isFirstResponder ? 'xl' : undefined}
                            control={form.control}
                        />
                    )}
                </Flex>
                <Flex flex={1} flexFlow="wrap" gap={4}>
                    <Box w={INPUT_PERCENTAGE_WIDTH_SM} minWidth={INPUT_WIDTH_SM}>
                        <FormSelect
                            name="locations.0.subdivisions"
                            label="Location Subdivisions"
                            control={form.control}
                            options={options.groupedSubdivisionOptions}
                            isMultiple
                            selectProps={{
                                showSelectedGroup: false,
                            }}
                        />
                    </Box>
                    <Box w={INPUT_PERCENTAGE_WIDTH_SM} minWidth={INPUT_WIDTH_SM}>
                        <FormInput name="locations.0.city" label="City" control={form.control} />
                    </Box>
                    <Box w={INPUT_PERCENTAGE_WIDTH_SM} minWidth={INPUT_WIDTH_SM}>
                        <FormSelect
                            name="locations.0.state"
                            label="State"
                            options={options.stateOptions}
                            control={form.control}
                        />
                    </Box>
                    <Box w={INPUT_PERCENTAGE_WIDTH_SM} minWidth={INPUT_WIDTH_SM}>
                        <FormInput
                            name="locations.0.zip"
                            label="ZIP"
                            control={form.control}
                            excludeFromFuzzySearch
                        />
                    </Box>
                    <Box w={INPUT_PERCENTAGE_WIDTH_SM} minWidth={INPUT_WIDTH_SM}>
                        <FormSelect
                            name="locations.0.country"
                            label="Country"
                            options={options.countryCodeOptions}
                            control={form.control}
                        />
                    </Box>
                </Flex>
                <Flex flex={1} flexFlow="wrap" gap={4}>
                    <Box w={INPUT_PERCENTAGE_WIDTH_MD} minWidth={INPUT_WIDTH_MD}>
                        <FormInput
                            name="locations.0.intersectionStreet1"
                            label="Intersection Street 1"
                            control={form.control}
                        />
                    </Box>
                    <Box w={INPUT_PERCENTAGE_WIDTH_MD} minWidth={INPUT_WIDTH_MD}>
                        <FormInput
                            name="locations.0.intersectionStreet2"
                            label="Intersection Street 2"
                            control={form.control}
                        />
                    </Box>
                </Flex>
            </FormSection>
            <HStack>
                {!hideLocationVerificationField && (
                    <>
                        <Box>
                            <FormRadioGroup
                                name="locationVerification"
                                items={options.locationVerificationOptions}
                                control={form.control}
                            />
                        </Box>
                        <Button
                            variant="ghost"
                            onClick={() => form.resetField('locationVerification')}
                        >
                            Clear
                        </Button>
                    </>
                )}
                <Box>
                    <Button
                        variant="ghost"
                        aria-label="hide / show location details"
                        leadingVisual={showDetails ? 'Close' : 'Open'}
                        onClick={() => setShowDetails(!showDetails)}
                    >{`${showDetails ? 'Hide' : 'Show'} Details`}</Button>
                </Box>
            </HStack>
            {showDetails && (
                <VStack align="left" gap={4}>
                    <SearchFormRadiusSearchSection form={form} />
                    <SearchFormPolygonSearchSection form={form} />
                </VStack>
            )}
        </VStack>
    );
}
